export type MealPeriod = 'breakfast' | 'lunch' | 'dinner';
export type ItemMealCategory = 'main_course' | 'appetizer' | 'dessert' | 'buffet' | 'menu_choice' | 'drink' | 'other' | 'children';
export type MealPlanItemStatus = 'included' | 'extra' | 'none';

export interface Seat {
    id: string;
    seatNumber: number;
    type: 'hotel_guest' | 'walk_in';
    guestName?: string;

    // Walk-in field: link to TableView Customer
    customerId?: string;

    // Hotel guest fields
    roomNumber?: string;
    roomWithStatusId?: string;
    reservationNumber?: string;
    reservationId?: string;
    rateId?: string;
    rateName?: string;
    checkInDate?: string;
    checkOutDate?: string;
    guestCount?: number;

    // Captured at order creation so time shifts don't change the recorded period
    capturedMealPeriod?: MealPeriod;
    // Local calendar date ("YYYY-MM-DD") the seat was created on. The allowance is keyed by it,
    // and the API records the consumption, so the date has to travel with the seat — the API
    // may be running in a different timezone than the property.
    capturedDate?: string;
}

// Meal plan configuration fetched from API (matches MealPlanConfigurationDto)
export interface MealPlanConfig {
    id: string;
    rateIds: string[];
    rateNames: string[];
    name: string;
    mealPeriods: MealPeriodConfig[];
    checkinDayPolicy: string;
    checkoutDayPolicy: string;
    doubleDipPrevention: boolean;
    optimizationDirection: string;
    drinksIncluded: boolean;
    isActive: boolean;
}

export interface MealPeriodConfig {
    id: string;
    mealPeriod: string;
    isEnabled: boolean;
    timeWindowStart: string;
    timeWindowEnd: string;
    chargeFullAfterPeriod?: boolean;
    rules: MealPlanRuleConfig[];
}

export interface MealPlanRuleConfig {
    id: string;
    mealCategory: string;
    maxQuantity: number;
}

export interface MealPlanAllowance {
    mealPeriod: MealPeriod;
    includedItems: AllowanceRule[];
    drinksIncluded: boolean;
    timeWindow: { start: string; end: string };
}

export interface AllowanceRule {
    category: ItemMealCategory;
    maxQuantity: number;
}

export interface MealConsumption {
    seatId: string;
    guestName: string;
    roomNumber: string;
    mealPeriod: MealPeriod;
    date: string; // YYYY-MM-DD
    itemsConsumed: ConsumedItem[];
    isAllowanceUsed: boolean;
    orderId?: string;
    reservationNumber?: string;
}

export interface ConsumedItem {
    orderProductId: string;
    category: ItemMealCategory;
    name: string;
    price: number;
    isIncludedInPlan: boolean;
}

// ─── Display helpers ─────────────────────────────────────────────────────────

export function getMealPlanLabel(rateName?: string): string {
    return rateName || 'Unknown Plan';
}

export function getMealPlanShortLabel(rateName?: string): string {
    if (!rateName) return '?';
    // Try to abbreviate common names
    const lower = rateName.toLowerCase();
    if (lower.includes('full board')) return 'FB';
    if (lower.includes('half board')) return 'HB';
    if (lower.includes('bed') && lower.includes('breakfast')) return 'BB';
    if (lower.includes('room only')) return 'RO';
    // Return first letters of each word
    return rateName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 3);
}

export function getMealPeriodLabel(period: string): string {
    switch (period) {
        case 'breakfast': return 'Breakfast';
        case 'lunch': return 'Lunch';
        case 'dinner': return 'Dinner';
        default: return period;
    }
}

// Result of meal plan evaluation for a single item
export interface MealPlanEvaluation {
    orderProductId: string;
    status: MealPlanItemStatus;
    reason?: string; // e.g., "Drinks not included", "Allowance exceeded"
}

// Result of evaluating all items for a seat
export interface SeatMealPlanResult {
    seatId: string;
    currentMealPeriod: MealPeriod | null;
    isWithinTimeWindow: boolean;
    isAllowanceAlreadyUsed: boolean;
    evaluations: MealPlanEvaluation[];
    totalIncluded: number;
    totalExtra: number;
    allowanceSummary: AllowanceSummaryItem[];
    warnings: string[];
}

export interface AllowanceSummaryItem {
    category: ItemMealCategory;
    label: string;
    used: number;
    max: number;
}
